/**
 * Reine Hilfen rund um die zentrale Ablage erzeugter Unterlagen (Stufe 1A).
 *
 * Die Module (SiGe-Plan, Handbuch, …) brauchen an drei Stellen dasselbe:
 *   1. einen STABILEN Inhaltsstand, damit derselbe Stand nicht zweimal in der
 *      Ablage liegt (`fassung_ref`),
 *   2. die Groessenpruefung VOR dem Upload — der Bucket nimmt hoechstens 50 MB,
 *      und ein abgelehnter Upload nach zwei Minuten Hochladen ist eine
 *      schlechte Meldung,
 *   3. eine einheitliche Lesart des Ergebnisses fuer die Oberflaeche.
 *
 * Hier steht nichts, was Supabase kennt — die Datei ist ohne DOM testbar.
 * Den Ablageweg selbst kennt `unterlagenAblage.ts`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// ZEITLIMIT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Wie lange ein Nebenlesen (Steckbrief, Stammdaten, frueherer Anker) vor
 * dem Upload hoechstens dauern darf. Haengt das Netz, soll die Ablage mit
 * einem Hinweis weiterlaufen, statt den Upload festzuhalten
 * (Codex-Review 17.10.2026).
 */
export const ZEITLIMIT_LESEN_MS = 15_000;

export class ZeitlimitFehler extends Error {
    constructor(ms: number) {
        super(`Keine Antwort innerhalb von ${Math.round(ms / 1000)} Sekunden`);
        this.name = 'ZeitlimitFehler';
    }
}

/**
 * Loest mit dem Wert des Versprechens auf — oder lehnt mit `ZeitlimitFehler`
 * ab, wenn es laenger als `ms` dauert. Der Zeitgeber wird in jedem Fall
 * aufgeraeumt; das Versprechen selbst laeuft weiter (abbrechen kann man es
 * nicht), sein spaetes Ergebnis wird verworfen.
 */
export const mitZeitlimit = <T>(versprechen: Promise<T>, ms: number = ZEITLIMIT_LESEN_MS): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        let erledigt = false;
        const zeitgeber = setTimeout(() => {
            if (erledigt) return;
            erledigt = true;
            reject(new ZeitlimitFehler(ms));
        }, ms);
        versprechen.then(
            wert => { if (!erledigt) { erledigt = true; clearTimeout(zeitgeber); resolve(wert); } },
            fehler => { if (!erledigt) { erledigt = true; clearTimeout(zeitgeber); reject(fehler); } },
        );
    });

// ═══════════════════════════════════════════════════════════════════════════
// INHALTSSTAND
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Spalten, die die Datenbank verwaltet und die NICHTS ueber den Inhalt
 * sagen. Ein `updated_at`, das sich beim Speichern desselben Textes aendert,
 * machte aus einem Stand zwei.
 */
const VERWALTETE_SPALTEN = new Set(['created_at', 'updated_at', 'deleted_at']);

/**
 * Serialisiert einen Wert mit SORTIERTEN Schluesseln.
 *
 * `JSON.stringify` haelt die Einfuegereihenfolge der Schluessel fest — und
 * die haengt davon ab, ob eine Zeile aus der Datenbank kam oder im Client
 * zusammengesetzt wurde. Derselbe Inhalt ergaebe zwei verschiedene Texte.
 * `undefined` und die verwalteten Spalten fallen weg; `null` bleibt, denn
 * „kein Bauherr" ist eine Angabe.
 */
export const stabileSerialisierung = (wert: unknown): string => {
    const schreibe = (w: unknown): string => {
        if (w === null) return 'null';
        if (w === undefined) return 'null';
        if (typeof w === 'number') return Number.isFinite(w) ? String(w) : 'null';
        if (typeof w === 'boolean') return w ? 'true' : 'false';
        if (typeof w === 'string') return JSON.stringify(w);
        if (Array.isArray(w)) return `[${w.map(schreibe).join(',')}]`;
        if (typeof w === 'object') {
            const o = w as Record<string, unknown>;
            const schluessel = Object.keys(o)
                .filter(k => !VERWALTETE_SPALTEN.has(k) && o[k] !== undefined)
                .sort();
            return `{${schluessel.map(k => `${JSON.stringify(k)}:${schreibe(o[k])}`).join(',')}}`;
        }
        // Funktionen, Symbole: tragen keinen Inhalt.
        return 'null';
    };
    return schreibe(wert);
};

/**
 * Ein kurzer, stabiler Hash ueber einen beliebigen Wert (53 Bit, als
 * Hexadezimalzahl).
 *
 * Zwei 32-Bit-Mischlaeufe mit verschiedenen Startwerten, am Ende verschraenkt
 * — kein kryptografischer Hash, der hier auch nichts verloren haette: es geht
 * darum, zwei gleiche Staende als gleich und zwei verschiedene mit sehr
 * hoher Wahrscheinlichkeit als verschieden zu erkennen. Eine Kollision
 * hiesse nur, dass die Ablage eine Fassung faelschlich als „schon abgelegt"
 * meldet — sichtbar, nicht still.
 */
export const stabilerInhaltsHash = (wert: unknown, saat = 0): string => {
    const text = stabileSerialisierung(wert);
    let h1 = 0xdeadbeef ^ saat;
    let h2 = 0x41c6ce57 ^ saat;
    for (let i = 0; i < text.length; i += 1) {
        const zeichen = text.charCodeAt(i);
        h1 = Math.imul(h1 ^ zeichen, 2654435761);
        h2 = Math.imul(h2 ^ zeichen, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const zahl = 4294967296 * (2097151 & h2) + (h1 >>> 0);
    return zahl.toString(16).padStart(14, '0');
};

// ═══════════════════════════════════════════════════════════════════════════
// GROESSENPRUEFUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Dateigrenze der Projektablage (Bucket `projekt-dokumente`): 50 MB. */
export const ABLAGE_GRENZE_BYTES = 50 * 1024 * 1024;

/** „12,3 MB" bzw. „840 KB" — fuer Meldungen. */
export const formatiereGroesse = (bytes: number): string => {
    const b = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
    if (b >= 1024 * 1024) {
        return `${(b / (1024 * 1024)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
    }
    return `${Math.ceil(b / 1024).toLocaleString('de-DE')} KB`;
};

/**
 * Der Diskriminator ist eine ZEICHENKETTE und kein boolean: das Projekt
 * uebersetzt ohne `strictNullChecks`, und dort verengt `if (!x.passt)` den
 * Typ nicht — `grund` waere am Aufrufer nicht lesbar (Muster wie
 * `asplanAblage.ts`).
 */
export type GroessenPruefung =
    | { status: 'ok'; bytes: number }
    | { status: 'fehler'; bytes: number; grund: string };

/**
 * Passt die Datei in die Ablage?
 *
 * Geprueft wird VOR dem Upload und mit einer Meldung, die sagt, was zu tun
 * ist. Beim SiGe-Plan ist die Stellschraube die Schrift (kleinere Schrift =
 * kuerzeres Blatt) und die Zahl der Piktogramme; das PDF selbst ist schon
 * komprimiert (`compress: true` im Generator) — „noch einmal komprimieren"
 * waere also ein leerer Rat.
 */
export const pruefeAblageGroesse = (
    bytes: number,
    grenze: number = ABLAGE_GRENZE_BYTES,
): GroessenPruefung => {
    const b = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
    if (b === 0) {
        return { status: 'fehler', bytes: b, grund: 'Die Datei ist leer — es gibt nichts abzulegen.' };
    }
    if (b > grenze) {
        return {
            status: 'fehler',
            bytes: b,
            grund: `Die Datei ist ${formatiereGroesse(b)} gross, die Ablage nimmt hoechstens `
                + `${formatiereGroesse(grenze)}. Sie wurde NICHT abgelegt.`,
        };
    }
    return { status: 'ok', bytes: b };
};

// ═══════════════════════════════════════════════════════════════════════════
// RUECKMELDUNG AN DIE OBERFLAECHE
// ═══════════════════════════════════════════════════════════════════════════

/** Das Ergebnis der zentralen Ablage, soweit die Oberflaeche es braucht. */
export interface AblageErgebnisKern {
    status: 'abgelegt' | 'schon_abgelegt' | 'fehler';
    /**
     * Klartext bei `fehler`. Bei den anderen Staenden nur gesetzt, wenn trotz
     * Erfolg etwas gemeldet gehoert — etwa eine doppelt hochgeladene Datei,
     * die nicht wieder entfernt werden konnte (`verwaisteDatei`).
     */
    fehler?: string | null;
    dokument?: { titel?: string | null } | null;
    /** Pfad einer Datei, die im Bucket liegen blieb, obwohl sie entfernt werden sollte. */
    verwaisteDatei?: string | null;
}

/**
 * Was die Oberflaeche ueber den letzten Ablageversuch weiss.
 *
 * `fehler` traegt den Grund im Klartext; `titel` ist der Name, unter dem die
 * Zeile in der Ablage steht — der Leser soll sie dort wiederfinden.
 * `warnung` ist ein Hinweis TROTZ Erfolg (verwaiste Datei): die Fassung
 * liegt, aber etwas gehoert gemeldet — ein uneingeschraenktes Gruen waere
 * hier eine Unterschlagung (Codex-Review 17.10.2026).
 */
export type AblageStand =
    | { art: 'laeuft' }
    | { art: 'abgelegt'; titel: string; schon: boolean; warnung: string | null }
    | { art: 'fehler'; fehler: string };

/** Die Farbe, in der ein Stand gezeigt wird — EINE Lesart fuer alle Module. */
export type AblageTon = 'laeuft' | 'erfolg' | 'warnung' | 'fehler';

export const ablageStandTon = (stand: AblageStand | null | undefined): AblageTon => {
    if (!stand || stand.art === 'laeuft') return 'laeuft';
    if (stand.art === 'fehler') return 'fehler';
    return stand.warnung ? 'warnung' : 'erfolg';
};

/**
 * Der Hinweis trotz Erfolg: der Satz des Kerns, dazu der Pfad der verwaisten
 * Datei, falls der Satz ihn nicht schon nennt. `null`, wenn nichts zu melden ist.
 */
const warnungAus = (ergebnis: AblageErgebnisKern): string | null => {
    const satz = (ergebnis.fehler || '').trim();
    const pfad = (ergebnis.verwaisteDatei || '').trim();
    const pfadTeil = pfad && !satz.includes(pfad) ? `Verwaiste Datei im Speicher: ${pfad}` : '';
    return [satz, pfadTeil].filter(Boolean).join(' ') || null;
};

/** Aus dem Ergebnis der Ablage den Stand fuer die Anzeige bilden. */
export const ablageStandAus = (ergebnis: AblageErgebnisKern, titel: string): AblageStand => {
    if (ergebnis.status === 'fehler') {
        return { art: 'fehler', fehler: (ergebnis.fehler || '').trim() || 'Unbekannter Fehler.' };
    }
    return {
        art: 'abgelegt',
        titel: (ergebnis.dokument?.titel || '').trim() || titel,
        schon: ergebnis.status === 'schon_abgelegt',
        warnung: warnungAus(ergebnis),
    };
};

/** Ein gescheiterter Versuch, der gar nicht bis zur Ablage kam (Groesse, PDF). */
export const ablageFehler = (grund: string): AblageStand => ({
    art: 'fehler',
    fehler: (grund || '').trim() || 'Unbekannter Fehler.',
});

/** Der Satz zur Anzeige. */
export const ablageStandText = (stand: AblageStand | null | undefined): string => {
    if (!stand) return '';
    if (stand.art === 'laeuft') return 'Wird in Dokumente abgelegt …';
    if (stand.art === 'fehler') return `Nicht in Dokumente abgelegt: ${stand.fehler}`;
    const satz = stand.schon
        ? `Dieser Stand liegt bereits in Dokumenten: „${stand.titel}".`
        : `In Dokumente abgelegt: „${stand.titel}".`;
    return stand.warnung ? `${satz} Hinweis: ${stand.warnung}` : satz;
};
